import { ArrowDropDown, Check } from "@mui/icons-material";
import {
  IconButton,
  Stack,
  Paper,
  Popper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  Divider,
  Typography,
} from "@mui/material";
import { ApiAttributionModel } from "@incendium/api";
import { AttributionMetric } from "features/analytics/types/types";
import { attributionMetrics } from "features/attributionAnalytics/constants";
import { useState, useCallback } from "react";
import { formatEnumVal } from "Helpers/enumToText";

const StyledPopper = styled(Popper)(() => ({
  zIndex: 2,
}));

const StyledListButton = styled(ListItemButton)(({ theme }) => ({
  "& svg:not(.MuiSvgIcon-root)": {
    width: 40,
    fill: theme.palette.primary.dark,
  },
  "& .MuiListItemText-root": {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
}));

interface ConversionMetricHeadProps {
  conversionMetric: AttributionMetric;
  attributionModel: ApiAttributionModel;
  setConversionMetric: (metric: AttributionMetric) => void;
}

function ConversionMetricHead({
  conversionMetric,
  setConversionMetric,
  attributionModel,
}: ConversionMetricHeadProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl((prev) =>
      !event || prev === event.currentTarget ? null : event.currentTarget,
    );
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleMetricChange = useCallback(
    (metric: AttributionMetric) => {
      setConversionMetric(metric);
      setAnchorEl(null);
    },
    [setConversionMetric],
  );

  return (
    <>
      <Stack direction="row" alignItems={"center"}>
        <strong>
          {attributionMetrics[conversionMetric]} (
          {formatEnumVal(attributionModel as string)})
        </strong>
        <IconButton onClick={handleClick}>
          <ArrowDropDown
            sx={{
              transition: "transform 0.15s linear",
              transform: isOpen ? "rotate(180deg)" : undefined,
            }}
          />
        </IconButton>
      </Stack>

      <StyledPopper open={isOpen} anchorEl={anchorEl} placement="bottom-end">
        <Paper>
          <ClickAwayListener onClickAway={handleClose}>
            <List disablePadding>
              <ListItem>
                <Typography variant="subtitle2" my={1}>
                  Conversion Metrics
                </Typography>
              </ListItem>
              <Divider />
              {Object.keys(attributionMetrics).map((k) => (
                <ListItem key={k} disablePadding>
                  <StyledListButton
                    onClick={() => handleMetricChange(k as AttributionMetric)}
                  >
                    <ListItemText primary={attributionMetrics[k]} />
                    {conversionMetric === k && (
                      <ListItemIcon>
                        <Check color="success" />
                      </ListItemIcon>
                    )}
                  </StyledListButton>
                </ListItem>
              ))}
            </List>
          </ClickAwayListener>
        </Paper>
      </StyledPopper>
    </>
  );
}

export default ConversionMetricHead;
